// Separates counsel from the outcome in the text after the carriage lawyer on a diary line.
// Shared by both LACW diary readers (lacw_diary_node_parser.mjs, src/lib/standaloneServices.js).
//
// Diary wording it has to handle (names made up):
//   "Jane Doe briefed"                         counsel Jane Doe
//   "Jane Doe briefed Matter proceeded as ..." counsel Jane Doe, outcome "Matter proceeded as ..."
//   "briefed as counsel. Matters resolved"     counsel is the carriage lawyer, outcome the rest
//   "Counsel appeared: Jane Doe - Adjourned"   counsel Jane Doe, outcome "Adjourned"
//   "(Counsel briefed)", "BU briefed counsel"  counsel appeared, not named
//   "Sam has briefed other lawyer - ..."       not counsel: all of it is outcome
//
// Merely mentioning counsel or a briefing does not make a line a counsel line, and only the
// counsel words are taken: whatever follows them is the outcome.

const NAME = String.raw`[A-Z][\p{L}'’.-]*(?:\s+[A-Z][\p{L}'’.-]*){0,3}`;
const STOP = String.raw`(?=$|[\s.,;:)])`;

const NAMED = "named";
const UNNAMED = "unnamed";
const LAWYER = "lawyer";

// Tried in order against the start of the text. The first group, if any, is counsel's name.
const PATTERNS = [
  [new RegExp(String.raw`^[Cc]ounsel\s+(?:appeared|briefed)\s*[:-]\s*(${NAME})`, "u"), NAMED],
  [new RegExp(String.raw`^[Cc]ounsel\s+(?:appeared|briefed)\s+(${NAME})(?=$|\s+-\s+)`, "u"), NAMED],
  [new RegExp(String.raw`^\(?\s*(?:[Cc]ounsel|[Bb]arrister)\s+(?:was\s+)?(?:appeared|briefed)\s*\)?${STOP}`, "u"), UNNAMED],
  [new RegExp(String.raw`^${NAME}\s+briefed\s+(?:counsel|a\s+barrister)${STOP}`, "u"), UNNAMED],
  [new RegExp(String.raw`^(${NAME})\s+(?:appeared\s+as\s+counsel|appeared\s+for\s+the\s+client)${STOP}`, "u"), NAMED],
  [new RegExp(String.raw`^(${NAME})\s+\(?briefed\)?(?:\s+as\s+counsel|\s+to\s+appear)?${STOP}`, "u"), NAMED],
  [new RegExp(String.raw`^\(?briefed\)?(?:\s+as\s+counsel|\s+to\s+appear)?${STOP}`, "u"), LAWYER],
];

// Capitalised words that can sit where a name goes but are not anyone's name.
const GENERIC = /^(?:counsel|barrister)$/i;
const NOT_A_NAME = /^(?:client|matter|lacw|solicitor|lawyer|court)$/i;

const clean = (value = "") => String(value || "").replace(/\s+/g, " ").trim();
const trimLead = (value = "") => clean(value).replace(/^(?:[-.,;:]\s*)+/, "").trim();

function counselLabel(name = "") {
  return name && !GENERIC.test(name) ? `Counsel appeared - ${name}` : "Counsel appeared";
}

function readCounsel(text, lawyer) {
  for (const [pattern, kind] of PATTERNS) {
    const match = text.match(pattern);
    if (!match) continue;
    const name = clean(match[1]).replace(/[.,;:]+$/, "");
    if (kind === NAMED && NOT_A_NAME.test(name)) return null;
    const label = kind === NAMED ? counselLabel(name)
      : kind === LAWYER ? counselLabel(lawyer)
      : counselLabel();
    return { counsel: label, rest: trimLead(text.slice(match[0].length)) };
  }
  return null;
}

// "Further plea (Jane Doe briefed)" or "Plea, Jane Doe (briefed)": counsel named in the listing
// itself. The listing is left as it is.
export function counselFromListing(listing = "") {
  const text = clean(listing);
  const match = text.match(new RegExp(String.raw`\((${NAME})\s+briefed\)`, "u"))
    || text.match(new RegExp(String.raw`(${NAME})\s+\(briefed\)`, "u"));
  if (!match) return "";
  const name = clean(match[1]).replace(/^PH\s+/, "");
  return NOT_A_NAME.test(name) ? "" : counselLabel(name);
}

// remainder: the diary text after the carriage lawyer. Returns { counsel, outcome }, either
// of which can be "".
export function splitCounselFromOutcome(remainder = "", lawyer = "") {
  let text = trimLead(remainder);

  // "(Formerly CC) Jane Doe briefed": a note about carriage, not part of counsel's name.
  let carriageNote = "";
  const note = text.match(/^\((?:formerly|was|now|previously)\b[^)]*\)\s*/i);
  if (note) {
    carriageNote = clean(note[0]);
    text = text.slice(note[0].length);
  }
  const withNote = (outcome) => clean([outcome, carriageNote].filter(Boolean).join(" "));

  const first = readCounsel(text, lawyer);
  if (first) return { counsel: first.counsel, outcome: withNote(first.rest) };

  // Later in the line, counsel only counts as a segment of its own: "Adjourned - Jane Doe briefed".
  const segments = text.split(/\s+-\s+/).map(clean).filter(Boolean);
  for (let index = 1; index < segments.length; index += 1) {
    const found = readCounsel(segments[index], lawyer);
    if (found && !found.rest) {
      segments.splice(index, 1);
      return { counsel: found.counsel, outcome: withNote(segments.join(" - ")) };
    }
  }
  return { counsel: "", outcome: withNote(text) };
}
